import React, { useCallback, useContext, useMemo, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { themeClassName } from 'lib/theme/themeUtils'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BestOfSquareLogo from './BestOfSquareLogo'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BestOfBackgroundImage from './BestOfBackgroundImage'
import TextButton from 'components/Luxkit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { NEW_DEFAULT_OFFER_LIST } from 'home/constants'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import config from 'constants/config'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import Carousel from 'components/Luxkit/Carousel/Carousel'
import { BestOfCampaignRegionTags } from './BestOfConstants'
import GeoContext from 'contexts/geoContext'
import { useBestOf2024 } from './BestOfUtils'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'

const BestOfPane = styled(Pane)`
  position: relative;
`

const BestOfBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
`

const filterOptions = [
  { label: 'Bonus Dining & Drinks', value: 'Dining & Drinks' },
  { label: 'Family Friendly', value: 'Family Friendly' },
  { label: 'Sun & Beach', value: 'Sun & Beach' },
]

interface Props {
  className?: string;
}

function BestOfHeroOfferCarouselSection(props: Props) {
  const { className } = props
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()
  const { currentRegionCode } = useContext(GeoContext)
  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const nextFilter = e.currentTarget.dataset.value
    setSelectedFilter(value => value === nextFilter ? undefined : nextFilter)
  }, [])

  const isLuxPlusMember = useAppSelector(checkCanViewLuxPlusBenefits)

  const { wave } = useBestOf2024()

  const filters = useMemo<App.OfferListFilters>(() => {
    return {
      ...NEW_DEFAULT_OFFER_LIST,
      holidayTypes: selectedFilter ? [selectedFilter] : undefined,
      campaigns: [BestOfCampaignRegionTags[currentRegionCode]],
      isLuxPlusMember,
    }
  }, [selectedFilter, currentRegionCode, isLuxPlusMember])

  const searchUrl = useMemo(() => encodeSearchParams({
    searchItem: ANYWHERE_SEARCH_ITEM,
    rooms: [config.search.defaultOccupants],
    filters: {
      campaigns: [BestOfCampaignRegionTags[currentRegionCode]],
    },
  }), [currentRegionCode])

  return (
    <BestOfPane className={className}>
      <BestOfBackground>
        <BestOfBackgroundImage overlayStyle="hero" />
      </BestOfBackground>
      <Content>
        <Group direction="vertical" gap={24}>
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <Group
                direction="horizontal"
                horizontalAlign="space-between"
                verticalAlign="center"
                gap={16}
              >
                <Group direction="horizontal" gap={24} verticalAlign="center">
                  <BestOfSquareLogo height={68} />
                  <VerticalSpacer gap={2}>
                    <Heading variant="heading2" colour="neutral-eight">
                      {wave === 'first-wave-early-access' && <><u>Early access</u> to the best escapes </>}
                      {wave !== 'first-wave-early-access' && <>The best escapes are <u>back</u></>}
                    </Heading>
                    <BodyText
                      variant="large" colour="neutral-eight">
                      {wave === 'first-wave-early-access' && 'LuxPlus+ members get exclusive access before the general public'}
                      {wave !== 'first-wave-early-access' && 'LIMITED TIME ONLY'}
                    </BodyText>
                  </VerticalSpacer>
                </Group>
                <CSSBreakpoint min="tablet">
                  <Group direction="horizontal" gap={16} verticalAlign="center">
                    <CSSBreakpoint min="desktop">
                      <Group direction="horizontal" gap={8}>
                        {filterOptions.map(option =>
                          <FilterChip
                            key={option.value}
                            size="medium"
                            selected={option.value === selectedFilter}
                            data-value={option.value}
                            onClick={toggleFilter}
                          >
                            {option.label}
                          </FilterChip>,
                        )}
                      </Group>
                    </CSSBreakpoint>
                    <TextButton
                      kind="tertiary"
                      variant="ghost"
                      to={`/search?${searchUrl.toString()}`}
                    >
                      View all offers
                    </TextButton>
                  </Group>
                </CSSBreakpoint>
              </Group>
              <CSSBreakpoint max="tablet">
                <Carousel gap={8} snap="start">
                  {filterOptions.map(option =>
                    <FilterChip
                      key={option.value}
                      size="medium"
                      selected={option.value === selectedFilter}
                      data-value={option.value}
                      onClick={toggleFilter}
                    >
                      {option.label}
                    </FilterChip>,
                  )}
                </Carousel>
              </CSSBreakpoint>
            </VerticalSpacer>
          </LayoutContainer>
          <OfferListCarousel
            filters={filters}
            className={themeClassName('inverse')}
            tileStyle="card-medium"
          />
          <CSSBreakpoint max="mobile">
            <TextButton
              kind="tertiary"
              variant="ghost"
              to={`/search?${searchUrl.toString()}`}
            >
              View all offers
            </TextButton>
          </CSSBreakpoint>
        </Group>
      </Content>
    </BestOfPane>
  )
}

export default React.memo(BestOfHeroOfferCarouselSection)
